import { activeAlarmsSignal, findActiveAlarmForLeaf } from '../realtime/alarmsStore';

interface Props {
  leafId: string;
}

/**
 * Tiny inline warn/alarm marker for a single leaf. Reads
 * `activeAlarmsSignal.value` internally so ONLY this marker re-renders when
 * the alarm list changes — not the surrounding LeafRow, and definitely not
 * the sibling leaves in the plant tree.
 *
 * Design choice: the marker component itself is what subscribes to the
 * signal, not LeafRow. That keeps the 4999 leaves that don't have an alarm
 * out of the re-render set on every alarm event. On a 5000-leaf tree we
 * end up with 5000 tiny markers, each subscribed to one signal — but the
 * signal only fires when the list actually changes, and the marker's
 * render body is trivial (constant-time filter against a list that stays
 * small in practice, per concept "≤ hundreds").
 *
 * We deliberately do NOT use `computed()` per leaf — 5000 computed signals
 * would be more memory than the direct filter. The filter itself is
 * short-circuited by the leafId check.
 */
export function LeafAlarmMarker({ leafId }: Props) {
  const alarm = findActiveAlarmForLeaf(leafId, activeAlarmsSignal.value);
  if (!alarm) return null;

  const isAlarm = alarm.severity === 'Alarm';
  const tone = isAlarm ? 'error' : 'warn';
  const glyph = isAlarm ? '!' : '△';
  const tooltip = `${isAlarm ? 'Alarm' : 'Warnung'}: ${alarm.message}\n${alarm.triggeredAt}`;

  return (
    <span
      class={`hv-leaf-alarm hv-leaf-alarm--${tone}`}
      title={tooltip}
      aria-label={tooltip}
      role="img"
    >
      {glyph}
    </span>
  );
}
